import { useUserContext } from "@/context/user.context";
import {
  FEATURE_FLAGS,
  useFeatureIsOn,
  useGrowthBook,
} from "@/utils/growthbook";

export interface JobFeedAccess {
  /**
   * `true` once BOTH the user context AND GrowthBook have loaded. Callers
   * should show a loader while `false` — treating `hasAccess === false`
   * before ready causes legit users to flash the denied state or get
   * redirected off the route while feature definitions are still fetching.
   */
  isReady: boolean;
  /** `true` when the user is allowed to see the Job Feed tab / route. */
  hasAccess: boolean;
}

/**
 * Single gate for the Job Feed — the header tab and the `/job-feed` route
 * share it. Access = internal user OR the `job-feed-tab-enabled` GrowthBook
 * flag is on for this session's company (targeting is set in the GrowthBook
 * UI via the `companyId` attribute). Mirrors `useAtsSuiteAccess`.
 *
 * @returns readiness + access state; see `JobFeedAccess` for field semantics.
 */
export const useJobFeedAccess = (): JobFeedAccess => {
  const { initialized, isInternal } = useUserContext();
  const gb = useGrowthBook();
  const isJobFeedFlagEnabled: boolean = useFeatureIsOn(
    FEATURE_FLAGS.jobFeedTabEnabled,
  );

  const isReady: boolean = initialized && gb.ready;
  return {
    isReady,
    hasAccess: isReady && (isInternal || isJobFeedFlagEnabled),
  };
};
